import React, { useState } from 'react';
import {
  useDocumentTrackingHistory,
  useDocumentTrackingStats,
  type DocumentTrackingLog,
} from '../hooks/useDocumentTracking';
import { formatTime as formatColombiaTime } from '../utils/dateFormat';

// ============================================
// HELPERS
// ============================================

const getTodayStr = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatTime = (dateStr: string): string => {
  return formatColombiaTime(dateStr);
};

const formatCurrency = (amount: number): string => {
  return '$' + Math.round(amount).toLocaleString('es-CO');
};

const getCategoryLabel = (cat: string): string => {
  const labels: Record<string, string> = {
    'MOD_POS': 'POS Interno',
    'MOD_FORCE_ELECTRONIC': 'Forzada DIAN',
    'MOD_RULE_FAILED_CLIENT': 'Cliente Identificado',
    'MOD_RULE_FAILED_PAYMENT': 'Pago No Efectivo',
    'MOD_RULE_FAILED_MIXED': 'Pagos Mixtos',
    'MOD_RULE_FAILED_MULTIPLE': 'Múltiples Reglas',
    'ELECTRONIC_NORMAL': 'Electrónica Normal',
    'ELECTRONIC_FORCE_REDUNDANT': 'Forzada (sin efecto)',
  };
  return labels[cat] || cat;
};

const getCategoryColor = (cat: string): string => {
  if (cat === 'MOD_POS') return '#3b82f6';
  if (cat === 'MOD_FORCE_ELECTRONIC') return '#f59e0b';
  if (cat.startsWith('MOD_RULE_FAILED')) return '#8b5cf6';
  if (cat === 'ELECTRONIC_NORMAL') return '#10b981';
  if (cat === 'ELECTRONIC_FORCE_REDUNDANT') return '#6b7280';
  return '#64748b';
};

// ============================================
// COMPONENTES
// ============================================

const StatCard: React.FC<{ label: string; value: number; color: string; sub?: string }> = ({ label, value, color, sub }) => (
  <div className="dt-stat-card">
    <div className="dt-stat-value" style={{ color }}>{value}</div>
    <div className="dt-stat-label">{label}</div>
    {sub && <div className="dt-stat-sub">{sub}</div>}
  </div>
);

const DocumentRow: React.FC<{ doc: DocumentTrackingLog; expanded: boolean; onToggle: () => void }> = ({ doc, expanded, onToggle }) => {
  const statusColor = doc.transmitida ? '#10b981' : '#3b82f6';
  const statusText = doc.transmitida ? 'Transmitida' : 'POS';

  return (
    <>
      <tr className="dt-row" onClick={onToggle}>
        <td className="dt-cell-factura">
          <span className="dt-num-factura">{doc.numeroFactura}</span>
        </td>
        <td className="dt-cell-hora">{formatTime(doc.fechaCreacion)}</td>
        <td className="dt-cell-total">{formatCurrency(doc.total)}</td>
        <td>
          <span className="dt-badge" style={{ background: `${statusColor}15`, color: statusColor, borderColor: `${statusColor}30` }}>
            {statusText}
          </span>
        </td>
        <td className="dt-cell-cliente">{doc.clienteNombre || 'N/A'}</td>
        <td>
          <span className="dt-badge-pago">{doc.metodoPago || 'N/A'}</span>
        </td>
        <td>
          <span className={`dt-mod-indicator ${doc.modActivo ? 'on' : 'off'}`}>
            {doc.modActivo ? 'ON' : 'OFF'}
          </span>
        </td>
        <td className="dt-cell-motivo">
          <div className="dt-motivo-short">{doc.motivoCorto}</div>
          {doc.forceElectronic && <span className="dt-force-badge">FORZADA</span>}
        </td>
        <td className="dt-cell-chevron">{expanded ? '▲' : '▼'}</td>
      </tr>
      {expanded && (
        <tr className="dt-row-detail">
          <td colSpan={9}>
            <div className="dt-detail-content">
              <div className="dt-detail-section">
                <div className="dt-detail-title">Motivo Detallado</div>
                <div className="dt-detail-text">{doc.motivo}</div>
              </div>
              <div className="dt-detail-grid">
                <div className="dt-detail-item">
                  <span className="dt-detail-label">Categoría</span>
                  <span className="dt-detail-value" style={{ color: getCategoryColor(doc.categoriaDecision) }}>
                    {getCategoryLabel(doc.categoriaDecision)}
                  </span>
                </div>
                <div className="dt-detail-item">
                  <span className="dt-detail-label">Consumidor Final</span>
                  <span className="dt-detail-value">{doc.esConsumidorFinal ? 'Sí' : 'No'}</span>
                </div>
                <div className="dt-detail-item">
                  <span className="dt-detail-label">Solo Efectivo</span>
                  <span className="dt-detail-value">{doc.esSoloEfectivo ? 'Sí' : 'No'}</span>
                </div>
                <div className="dt-detail-item">
                  <span className="dt-detail-label">Pagos Mixtos</span>
                  <span className="dt-detail-value">{doc.pagosMixtos ? 'Sí' : 'No'}</span>
                </div>
                <div className="dt-detail-item">
                  <span className="dt-detail-label">CUFE</span>
                  <span className="dt-detail-value dt-cufe">{doc.cufe || 'N/A'}</span>
                </div>
                <div className="dt-detail-item">
                  <span className="dt-detail-label">Factus Status</span>
                  <span className="dt-detail-value">{doc.factusStatus || 'N/A'}</span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ============================================
// PÁGINA PRINCIPAL
// ============================================

export const DocumentTrackingPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(getTodayStr());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');

  const { documents, loading } = useDocumentTrackingHistory(selectedDate);
  const { stats } = useDocumentTrackingStats(selectedDate);

  const isToday = selectedDate === getTodayStr();

  // Filtrar documentos
  const filtered = documents.filter(d => {
    if (filterCategory !== 'ALL' && d.categoriaDecision !== filterCategory) return false;
    if (filterStatus === 'TRANSMITIDA' && !d.transmitida) return false;
    if (filterStatus === 'POS' && d.transmitida) return false;
    return true;
  });

  // Categorías únicas para el filtro
  const categories = [...new Set(documents.map(d => d.categoriaDecision))];

  return (
    <div className="dt-page">
      <style>{pageStyles}</style>

      {/* Header */}
      <div className="dt-header">
        <h2 className="dt-title">Seguimiento de Documentos</h2>
        <p className="dt-subtitle">Trazabilidad de cada decisión del sistema MOD</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="dt-stats-row">
          <StatCard label="Total Documentos" value={stats.totalDocumentos} color="#1e293b" />
          <StatCard label="Transmitidas DIAN" value={stats.transmitidas} color="#10b981"
            sub={stats.totalDocumentos > 0 ? `${Math.round((stats.transmitidas / stats.totalDocumentos) * 100)}%` : ''} />
          <StatCard label="POS Internas" value={stats.noTransmitidas} color="#3b82f6"
            sub={stats.totalDocumentos > 0 ? `${Math.round((stats.noTransmitidas / stats.totalDocumentos) * 100)}%` : ''} />
        </div>
      )}

      {/* Toolbar */}
      <div className="dt-toolbar">
        <div className="dt-toolbar-left">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="dt-date-input"
          />
          {!isToday && (
            <button className="dt-today-btn" onClick={() => setSelectedDate(getTodayStr())}>HOY</button>
          )}
        </div>
        <div className="dt-toolbar-right">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="dt-select">
            <option value="ALL">Todas</option>
            <option value="TRANSMITIDA">Transmitidas</option>
            <option value="POS">POS Internas</option>
          </select>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="dt-select">
            <option value="ALL">Todas las categorías</option>
            {categories.map(c => (
              <option key={c} value={c}>{getCategoryLabel(c)}</option>
            ))}
          </select>
          <span className="dt-count">{filtered.length} de {documents.length}</span>
        </div>
      </div>

      {/* Table */}
      {loading && documents.length === 0 ? (
        <div className="dt-loading">Cargando documentos...</div>
      ) : filtered.length === 0 ? (
        <div className="dt-empty">
          <div className="dt-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="dt-empty-title">No hay documentos para esta fecha</div>
          <div className="dt-empty-sub">Los documentos se sincronizan automáticamente cada 30 segundos.</div>
        </div>
      ) : (
        <div className="dt-table-container">
          <table className="dt-table">
            <thead>
              <tr>
                <th>N° Factura</th>
                <th>Hora</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Cliente</th>
                <th>Pago</th>
                <th>MOD</th>
                <th>Motivo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  expanded={expandedId === doc.id}
                  onToggle={() => setExpandedId(expandedId === doc.id ? null : doc.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================
// ESTILOS
// ============================================

const pageStyles = `
  .dt-page { padding: 20px; }

  .dt-header { margin-bottom: 20px; }
  .dt-title { font-size: 1.4rem; font-weight: 700; color: #0f172a; margin: 0; }
  .dt-subtitle { font-size: 0.85rem; color: #64748b; margin: 4px 0 0; }

  /* Stats */
  .dt-stats-row { display: flex; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
  .dt-stat-card {
    flex: 1; min-width: 150px; padding: 16px 20px;
    background: white; border: 1px solid #e2e8f0; border-radius: 8px;
    text-align: center;
  }
  .dt-stat-value { font-size: 1.8rem; font-weight: 800; font-variant-numeric: tabular-nums; }
  .dt-stat-label { font-size: 0.75rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }
  .dt-stat-sub { font-size: 0.8rem; color: #94a3b8; margin-top: 2px; }

  /* Toolbar */
  .dt-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
  .dt-toolbar-left, .dt-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

  .dt-date-input {
    padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 6px;
    background: white; color: #1e293b; font-size: 0.85rem; font-family: inherit;
  }
  .dt-date-input:focus { border-color: #3b82f6; outline: none; }

  .dt-today-btn {
    padding: 8px 14px; background: #3b82f6; color: white; border: none; border-radius: 6px;
    font-size: 0.75rem; font-weight: 600; cursor: pointer; letter-spacing: 0.5px;
  }
  .dt-today-btn:hover { background: #2563eb; }

  .dt-select {
    padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 6px;
    background: white; font-size: 0.8rem; font-family: inherit; color: #334155;
  }

  .dt-count { font-size: 0.8rem; color: #94a3b8; }

  /* Table */
  .dt-table-container { background: white; border: 1px solid #e2e8f0; border-radius: 8px; overflow-x: auto; }
  .dt-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; }

  .dt-table thead th {
    padding: 10px 12px; text-align: left; font-size: 0.7rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.5px; color: #64748b;
    border-bottom: 2px solid #e2e8f0; background: #f8fafc;
    white-space: nowrap;
  }

  .dt-row { cursor: pointer; transition: background 0.15s; }
  .dt-row:hover { background: #f8fafc; }
  .dt-row td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

  .dt-cell-factura { font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace; }
  .dt-num-factura { font-weight: 600; color: #1e293b; font-size: 0.8rem; }
  .dt-cell-hora { color: #64748b; white-space: nowrap; }
  .dt-cell-total { font-weight: 600; color: #0f172a; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .dt-cell-cliente { max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #475569; }
  .dt-cell-motivo { max-width: 220px; }
  .dt-cell-chevron { color: #94a3b8; font-size: 0.7rem; text-align: center; }

  /* Badges */
  .dt-badge {
    display: inline-flex; padding: 2px 8px; border-radius: 12px; font-size: 0.7rem;
    font-weight: 600; border: 1px solid; white-space: nowrap;
  }

  .dt-badge-pago {
    display: inline-flex; padding: 2px 6px; border-radius: 4px; font-size: 0.7rem;
    background: #f1f5f9; color: #475569;
  }

  .dt-mod-indicator {
    display: inline-flex; padding: 2px 8px; border-radius: 4px; font-size: 0.65rem;
    font-weight: 700; letter-spacing: 0.5px;
  }
  .dt-mod-indicator.on { background: #dcfce7; color: #166534; }
  .dt-mod-indicator.off { background: #f1f5f9; color: #94a3b8; }

  .dt-motivo-short { color: #475569; font-size: 0.78rem; line-height: 1.4; }

  .dt-force-badge {
    display: inline-flex; padding: 1px 6px; border-radius: 3px; font-size: 0.6rem;
    font-weight: 700; background: #fef3c7; color: #92400e; margin-top: 2px;
  }

  /* Expanded detail */
  .dt-row-detail td { padding: 0; border-bottom: 1px solid #e2e8f0; }
  .dt-detail-content { padding: 16px 20px; background: #f8fafc; }

  .dt-detail-section { margin-bottom: 12px; }
  .dt-detail-title { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #2563eb; margin-bottom: 6px; }
  .dt-detail-text { font-size: 0.82rem; color: #334155; line-height: 1.6; }

  .dt-detail-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .dt-detail-item { display: flex; flex-direction: column; gap: 2px; }
  .dt-detail-label { font-size: 0.65rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.3px; }
  .dt-detail-value { font-size: 0.8rem; color: #1e293b; font-weight: 500; }
  .dt-cufe { font-size: 0.65rem; font-family: monospace; word-break: break-all; color: #64748b; }

  /* Empty & Loading */
  .dt-loading { padding: 60px 20px; text-align: center; color: #64748b; }
  .dt-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; text-align: center; }
  .dt-empty-icon { color: #94a3b8; margin-bottom: 12px; opacity: 0.5; }
  .dt-empty-title { font-size: 1rem; font-weight: 600; color: #475569; }
  .dt-empty-sub { font-size: 0.8rem; color: #94a3b8; margin-top: 4px; }

  /* Responsive */
  @media (max-width: 768px) {
    .dt-detail-grid { grid-template-columns: repeat(2, 1fr); }
    .dt-stats-row { flex-direction: column; }
    .dt-cell-cliente, .dt-cell-motivo { display: none; }
  }
`;
